import type {
  ProposalChangeDescriptionContext,
  ProposalChangeDescriptor,
} from "work/proposals/negotiation/proposal-change-description";
import type {
  ProposalContentOperation,
  ProposalReviewerChange,
  SaveProposalDraftCommand,
} from "work/proposals/negotiation/proposal-collaboration-contract";
import { proposalFieldHasPendingChange } from "work/proposals/negotiation/proposal-field-pending-change";
import { describeProposalPendingChanges } from "work/proposals/_views/_shared/proposal-change-share-plan";
import type ProposalField from "work/proposals/proposal-fields/proposal-field";

/** Stable empty result, so a pane with nothing queued does not re-render on every store touch. */
const noProposalPendingChangePreview: ProposalChangeDescriptor[] = [];

export type ProposalPendingChangePreviewArgs = {
  /** The queue a Save has not been asked for yet. */
  operations?: readonly ProposalContentOperation[];
  reviewerChanges?: readonly ProposalReviewerChange[];
  /**
   * The commands a Save has already sent and is waiting on, read straight off the collaboration entry.
   *
   * Included because the queue empties the moment a command goes out, while the timeline only gains
   * the entry when the answer comes back: previewing the pending queue alone makes the row vanish for
   * the length of the round trip — the exact gap between editing and history this preview exists to
   * close. An acknowledged command is cleared from the entry by the returning envelope, which is also
   * what invalidates the timeline, so the preview hands over to the real entry rather than doubling it.
   */
  inFlightCommands?: Readonly<Record<string, SaveProposalDraftCommand | undefined>>;
  /**
   * Narrows the preview to one field control, for the per-field history popover. Left out by the
   * negotiation-wide Comments/Changes pane, which previews the whole queue.
   */
  field?: ProposalField | null;
  context?: ProposalChangeDescriptionContext;
};

/**
 * The change-log entries a Save is about to write, described before it is asked for (#5133).
 *
 * Proposal history only reached the Comments/Changes pane once the server had it, so a user editing a
 * proposal could not see what their edits would say until after they had committed them. These are the
 * same descriptors the Save confirmation lists — through `describeProposalPendingChanges`, so the
 * preview and the dialog cannot word or count the same queue differently — and the panel draws them
 * above the persisted stream, marked as unsaved.
 *
 * Field scoping reuses `proposalFieldHasPendingChange`, the rule that already decides whether a field's
 * history icon reads as pending, so an icon that says there is unsaved work here and the popover behind
 * it cannot disagree.
 */
export function buildProposalPendingChangePreview(
  args: Readonly<ProposalPendingChangePreviewArgs>,
): ProposalChangeDescriptor[] {
  const inFlight = Object.values(args.inFlightCommands ?? {}).filter(
    (command): command is SaveProposalDraftCommand => Boolean(command),
  );
  const queuedOperations = [...inFlight.flatMap((command) => command.operations), ...(args.operations ?? [])];
  const operations = args.field
    ? queuedOperations.filter((operation) => proposalFieldHasPendingChange([operation], args.field))
    : queuedOperations;
  // Reviewers are not proposal content and land on no field control, so only the negotiation-wide pane
  // previews them; a field's own popover would otherwise report a roster change as that field's history.
  const reviewerChanges = args.field
    ? []
    : [...inFlight.flatMap((command) => command.reviewerChanges), ...(args.reviewerChanges ?? [])];

  if (operations.length === 0 && reviewerChanges.length === 0) return noProposalPendingChangePreview;

  const descriptors = describeProposalPendingChanges({ operations, reviewerChanges, context: args.context });
  return descriptors.length === 0 ? noProposalPendingChangePreview : descriptors;
}
